import { createSlice } from "@reduxjs/toolkit";
import {
  AssignedConveyor,
  ILoginResponse,
  ISignUpResponse,
  IUserResponse,
} from "../../interfaces";
import {
  addNewStationAction,
  assignConveyorsAction,
  authDetailsInitializeAction,
  changePasswordAction,
  createPermissionAction,
  createRoleAction,
  deletePermissionAction,
  getAllPermissionsAction,
  getAllRoles,
  getAllUsers,
  getAssignedConveyorsAction,
  getLoginUserList,
  lockProfileAction,
  lockUserAccountAction,
  loginAction,
  logoutAction,
  resetUnlockAttemptsAction,
  setAccessCodeAction,
  setNfcCardAction,
  setUserRoleAction,
  signupAction,
  unlockProfileAction,
  unlockUserAccountAction,
  updatePermissionsAction,
  updateRoleAction,
  updateUserAction,
  validateAssignedConveyorAction,
} from "../actions/authAction";
import {
  IGetAllUserResponse,
  LoginUser,
  Role,
} from "../../typings/interfaces/responses/LoginUser";

export interface AuthState {
  user: IUserResponse | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  authDetailsInitialized: boolean;
  locked: boolean;
  loginResponse: ILoginResponse | null;
  loginUsers: LoginUser[];
  loginUsersLoading: boolean;
  loginUsersError: string | null;

  signupData: ISignUpResponse | null;
  signupLoading: boolean;
  signupError: string | null;

  allUsers: IGetAllUserResponse[];
  allUsersLoading: boolean;
  allUsersError: string | null;

  updateData: ISignUpResponse | null;
  updateLoading: boolean;
  updateError: string | null;

  updatePermissionsLoading: boolean;
  updatePermissionsError: string | null;
  updatePermissionsData: any;

  setRoleLoading: boolean;
  setRoleError: string | null;
  setRoleData: any;

  roles: Role[];
  rolesLoading: boolean;
  rolesError: string | null;

  permissions: { id: number; name: string; description: string }[];
  permissionsLoading: boolean;
  permissionsError: string | null;
  createPermissionLoading: boolean;
  deletePermissionLoading: boolean;

  setAccessCodeLoading: boolean;
  setAccessCodeError: string | null;

  setNfcCardLoading: boolean;
  setNfcCardError: string | null;

  accountLocked: boolean;
  lockUserLoading: boolean;
  lockUserError: string | null;
  unlockUserLoading: boolean;
  unlockUserError: string | null;
  resetUnlockAttemptsLoading: boolean;

  createRoleLoading: boolean;
  createRoleError: string | null;
  createRoleData: any;

  updateRoleLoading: boolean;
  updateRoleError: string | null;
  updateRoleData: any;

  addStationLoading: boolean;
  addStationError: string | null;
  addStationData: any;

  assignedConveyors: AssignedConveyor[];
  assignedConveyorsLoading: boolean;
  assignedConveyorsError: string | null;

  assignConveyorsLoading: boolean;
  assignConveyorsError: string | null;
  assignConveyorsData: string | null;

  validateAssignedConveyorLoading: boolean;
  validateAssignedConveyorError: string | null;
  validatedAssignedConveyor: boolean | null;
  validatedAssignedConveyorMessage: string | null;
  validatedAssignedConveyorStatusCode: number | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  loading: false,
  error: null,
  loginResponse: null,
  authDetailsInitialized: false,
  locked: false,
  loginUsers: [],
  loginUsersLoading: false,
  loginUsersError: null,

  signupData: null,
  signupLoading: false,
  signupError: null,

  allUsers: [],
  allUsersLoading: false,
  allUsersError: null,

  updateData: null,
  updateLoading: false,
  updateError: null,

  updatePermissionsLoading: false,
  updatePermissionsError: null,
  updatePermissionsData: null,

  setRoleLoading: false,
  setRoleError: null,
  setRoleData: null,

  roles: [],
  rolesLoading: false,
  rolesError: null,

  permissions: [],
  permissionsLoading: false,
  permissionsError: null,
  createPermissionLoading: false,
  deletePermissionLoading: false,

  setAccessCodeLoading: false,
  setAccessCodeError: null,

  setNfcCardLoading: false,
  setNfcCardError: null,

  accountLocked: false,
  lockUserLoading: false,
  lockUserError: null,
  unlockUserLoading: false,
  unlockUserError: null,
  resetUnlockAttemptsLoading: false,

  createRoleLoading: false,
  createRoleError: null,
  createRoleData: null,

  updateRoleLoading: false,
  updateRoleError: null,
  updateRoleData: null,

  addStationLoading: false,
  addStationError: null,
  addStationData: null,

  assignedConveyors: [],
  assignedConveyorsLoading: false,
  assignedConveyorsError: null,

  assignConveyorsLoading: false,
  assignConveyorsError: null,
  assignConveyorsData: null,

  validateAssignedConveyorLoading: false,
  validateAssignedConveyorError: null,
  validatedAssignedConveyor: null,

  validatedAssignedConveyorMessage: null,
  validatedAssignedConveyorStatusCode: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    clearAuthData: (state) => {
      state.user = null;
      state.token = null;
      state.loginResponse = null;
    },
    setLocked: (state, action) => {
      state.locked = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logoutAction.fulfilled, (state, action) => {
        state.user = null;
        state.token = null;
      })
      .addCase(loginAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.loginResponse = null;
      })
      .addCase(loginAction.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload.user;
        state.token = action.payload.token?.token || null;
        state.loginResponse = action.payload.response;
        state.locked = false;
      })
      .addCase(loginAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Something went wrong";
        state.loginResponse = null;
      })
      .addCase(authDetailsInitializeAction.pending, (state) => {
        // If want can show initializing global loader
      })
      .addCase(authDetailsInitializeAction.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload.user;
        state.token = action.payload.storedToken;
        state.loginResponse = null;
        state.authDetailsInitialized = true;
        state.locked = action.payload.profileLock;
        state.accountLocked = action.payload.accountLocked ?? false;
      })
      .addCase(authDetailsInitializeAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Something went wrong";
        state.loginResponse = null;
        state.authDetailsInitialized = true;
      })
      .addCase(changePasswordAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.loginResponse = null;
      })
      .addCase(changePasswordAction.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload.user;
        state.token = action.payload.token?.token || null;
        state.loginResponse = action.payload.response;
      })
      .addCase(changePasswordAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Something went wrong";
        state.loginResponse = null;
      })
      .addCase(lockProfileAction.fulfilled, (state) => {
        state.locked = true;
      })
      .addCase(unlockProfileAction.fulfilled, (state) => {
        state.locked = false;
      })
      .addCase(getLoginUserList.pending, (state) => {
        state.loginUsersLoading = true;
        state.loginUsersError = null;
        state.loginUsers = [];
      })
      .addCase(getLoginUserList.fulfilled, (state, action) => {
        state.loginUsersLoading = false;
        state.loginUsersError = null;
        state.loginUsers = action.payload || [];
      })
      .addCase(getLoginUserList.rejected, (state, action) => {
        state.loginUsersLoading = false;
        state.loginUsersError = action.error.message ?? "Something went wrong";
        state.loginUsers = [];
      })
      .addCase(signupAction.pending, (state) => {
        state.signupLoading = true;
        state.signupError = null;
        state.signupData = null;
      })
      .addCase(signupAction.fulfilled, (state, action) => {
        state.signupLoading = false;
        state.signupError = null;
        state.signupData = action.payload;
      })
      .addCase(signupAction.rejected, (state, action) => {
        state.signupLoading = false;
        state.signupError = (action.payload as string) ?? "Signup failed";
        state.signupData = null;
      })
      .addCase(getAllUsers.pending, (state) => {
        state.allUsersLoading = true;
        state.allUsersError = null;
        state.allUsers = [];
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.allUsersLoading = false;
        state.allUsersError = null;
        state.allUsers = action.payload || [];
      })

      .addCase(getAllUsers.rejected, (state, action) => {
        state.allUsersLoading = false;
        state.allUsersError =
          (action.payload as string) ?? "Failed to fetch users";
        state.allUsers = [];
      })
      .addCase(updateUserAction.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateData = null;
      })
      .addCase(updateUserAction.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateError = null;
        state.updateData = action.payload;
      })
      .addCase(updateUserAction.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = (action.payload as string) ?? "Update failed";
        state.updateData = null;
      });
    builder
      .addCase(updatePermissionsAction.pending, (state) => {
        state.updatePermissionsLoading = true;
        state.updatePermissionsError = null;
      })
      .addCase(updatePermissionsAction.fulfilled, (state, action) => {
        state.updatePermissionsLoading = false;
        state.updatePermissionsData = action.payload;
      })
      .addCase(updatePermissionsAction.rejected, (state, action) => {
        state.updatePermissionsLoading = false;
        state.updatePermissionsError = action.payload as string;
      });

    builder
      .addCase(setUserRoleAction.pending, (state) => {
        state.setRoleLoading = true;
        state.setRoleError = null;
      })
      .addCase(setUserRoleAction.fulfilled, (state, action) => {
        state.setRoleLoading = false;
        state.setRoleData = action.payload;
      })
      .addCase(setUserRoleAction.rejected, (state, action) => {
        state.setRoleLoading = false;
        state.setRoleError = action.payload as string;
      })
      .addCase(getAllRoles.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
        state.roles = [];
      })
      .addCase(getAllRoles.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.roles = action.payload;
      })
      .addCase(getAllRoles.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError =
          (action.payload as string) ?? "Failed to fetch roles";
        state.roles = [];
      })
      .addCase(getAllPermissionsAction.pending, (state) => {
        state.permissionsLoading = true;
        state.permissionsError = null;
      })
      .addCase(getAllPermissionsAction.fulfilled, (state, action) => {
        state.permissionsLoading = false;
        state.permissions = action.payload ?? [];
      })
      .addCase(getAllPermissionsAction.rejected, (state, action) => {
        state.permissionsLoading = false;
        state.permissionsError = action.payload as string;
      })
      .addCase(createPermissionAction.pending, (state) => {
        state.createPermissionLoading = true;
      })
      .addCase(createPermissionAction.fulfilled, (state) => {
        state.createPermissionLoading = false;
      })
      .addCase(createPermissionAction.rejected, (state) => {
        state.createPermissionLoading = false;
      })
      .addCase(deletePermissionAction.pending, (state) => {
        state.deletePermissionLoading = true;
      })
      .addCase(deletePermissionAction.fulfilled, (state, action) => {
        state.deletePermissionLoading = false;
        state.permissions = state.permissions.filter((p) => p.id !== action.payload.id);
      })
      .addCase(deletePermissionAction.rejected, (state) => {
        state.deletePermissionLoading = false;
      })
      .addCase(setAccessCodeAction.pending, (state) => {
        state.setAccessCodeLoading = true;
        state.setAccessCodeError = null;
      })
      .addCase(setAccessCodeAction.fulfilled, (state) => {
        state.setAccessCodeLoading = false;
        state.setAccessCodeError = null;
      })
      .addCase(setAccessCodeAction.rejected, (state, action) => {
        state.setAccessCodeLoading = false;
        state.setAccessCodeError = (action.payload as string) ?? "Failed to set access code";
      })
      .addCase(setNfcCardAction.pending, (state) => {
        state.setNfcCardLoading = true;
        state.setNfcCardError = null;
      })
      .addCase(setNfcCardAction.fulfilled, (state) => {
        state.setNfcCardLoading = false;
        state.setNfcCardError = null;
      })
      .addCase(setNfcCardAction.rejected, (state, action) => {
        state.setNfcCardLoading = false;
        state.setNfcCardError = (action.payload as string) ?? "Failed to assign NFC card";
      })
      .addCase(createRoleAction.pending, (state) => {
        state.createRoleLoading = true;
        state.createRoleError = null;
        state.createRoleData = null;
      })
      .addCase(createRoleAction.fulfilled, (state, action) => {
        state.createRoleLoading = false;
        state.createRoleError = null;
        state.createRoleData = action.payload;
      })
      .addCase(createRoleAction.rejected, (state, action) => {
        state.createRoleLoading = false;
        state.createRoleError =
          (action.payload as string) ?? "Create role failed";
        state.createRoleData = null;
      })
      .addCase(updateRoleAction.pending, (state) => {
        state.updateRoleLoading = true;
        state.updateRoleError = null;
        state.updateRoleData = null;
      })
      .addCase(updateRoleAction.fulfilled, (state, action) => {
        state.updateRoleLoading = false;
        state.updateRoleError = null;
        state.updateRoleData = action.payload;
      })
      .addCase(updateRoleAction.rejected, (state, action) => {
        state.updateRoleLoading = false;
        state.updateRoleError =
          (action.payload as string) ?? "Update role failed";
        state.updateRoleData = null;
      })
      .addCase(addNewStationAction.pending, (state) => {
        state.addStationLoading = true;
        state.addStationError = null;
        state.addStationData = null;
      })
      .addCase(addNewStationAction.fulfilled, (state, action) => {
        state.addStationLoading = false;
        state.addStationError = null;
        state.addStationData = action.payload;
      })
      .addCase(addNewStationAction.rejected, (state, action) => {
        state.addStationLoading = false;
        state.addStationError =
          (action.payload as string) ?? "Failed to add new station";
        state.addStationData = null;
      })
      .addCase(getAssignedConveyorsAction.pending, (state) => {
        state.assignedConveyorsLoading = true;
        state.assignedConveyorsError = null;
        state.assignedConveyors = [];
      })
      .addCase(getAssignedConveyorsAction.fulfilled, (state, action) => {
        state.assignedConveyorsLoading = false;
        state.assignedConveyors = action.payload;
      })
      .addCase(getAssignedConveyorsAction.rejected, (state, action) => {
        state.assignedConveyorsLoading = false;
        state.assignedConveyorsError =
          (action.payload as string) ?? "Failed to load assigned conveyors";
        state.assignedConveyors = [];
      })
      .addCase(assignConveyorsAction.pending, (state) => {
        state.assignConveyorsLoading = true;
        state.assignConveyorsError = null;
        state.assignConveyorsData = null;
      })
      .addCase(assignConveyorsAction.fulfilled, (state, action) => {
        state.assignConveyorsLoading = false;
        state.assignConveyorsData = action.payload;
      })
      .addCase(assignConveyorsAction.rejected, (state, action) => {
        state.assignConveyorsLoading = false;
        state.assignConveyorsError =
          (action.payload as string) ?? "Failed to assign conveyors";
        state.assignConveyorsData = null;
      })
      .addCase(validateAssignedConveyorAction.pending, (state) => {
        state.validateAssignedConveyorLoading = true;
        state.validateAssignedConveyorError = null;
        state.validatedAssignedConveyor = null;
        state.validatedAssignedConveyorMessage = null;
        state.validatedAssignedConveyorStatusCode = null;
      })
      .addCase(validateAssignedConveyorAction.fulfilled, (state, action) => {
        state.validateAssignedConveyorLoading = false;
        state.validateAssignedConveyorError = null;
        state.validatedAssignedConveyor = action.payload.body ?? null;
        state.validatedAssignedConveyorMessage = action.payload.message ?? null;

        state.validatedAssignedConveyorStatusCode =
          (action.payload as any)?.statusCode ?? null;
      })
      .addCase(validateAssignedConveyorAction.rejected, (state, action) => {
        state.validateAssignedConveyorLoading = false;
        const payload = action.payload as
          | { message: string; statusCode?: number }
          | undefined;
        state.validateAssignedConveyorError =
          payload?.message ?? action.error.message ?? "Validation failed";
        state.validatedAssignedConveyor = null;
        state.validatedAssignedConveyorMessage = payload?.message ?? null;
        state.validatedAssignedConveyorStatusCode = payload?.statusCode ?? null;
      })
      .addCase(lockUserAccountAction.pending, (state) => {
        state.lockUserLoading = true;
        state.lockUserError = null;
      })
      .addCase(lockUserAccountAction.fulfilled, (state) => {
        state.lockUserLoading = false;
        state.lockUserError = null;
      })
      .addCase(lockUserAccountAction.rejected, (state, action) => {
        state.lockUserLoading = false;
        state.lockUserError = (action.payload as string) ?? "Failed to lock account";
      })
      .addCase(unlockUserAccountAction.pending, (state) => {
        state.unlockUserLoading = true;
        state.unlockUserError = null;
      })
      .addCase(unlockUserAccountAction.fulfilled, (state) => {
        state.unlockUserLoading = false;
        state.unlockUserError = null;
        state.accountLocked = false;
      })
      .addCase(unlockUserAccountAction.rejected, (state, action) => {
        state.unlockUserLoading = false;
        state.unlockUserError = (action.payload as string) ?? "Failed to unlock account";
      })
      .addCase(resetUnlockAttemptsAction.pending, (state) => {
        state.resetUnlockAttemptsLoading = true;
      })
      .addCase(resetUnlockAttemptsAction.fulfilled, (state) => {
        state.resetUnlockAttemptsLoading = false;
      })
      .addCase(resetUnlockAttemptsAction.rejected, (state) => {
        state.resetUnlockAttemptsLoading = false;
      });
  },
});

export default authSlice.reducer;
export const AuthSliceAction = authSlice.actions;
